import { create } from 'zustand';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';
import type { SignInState } from '@/types/profile';

/**
 * The T-Bank fiscal receipt (54-FZ) needs an e-mail. An account created in
 * this browser (docs/identity/ADR-001) has none until it adds a sign-in
 * method, so the checkout asks for one first and sends it as `receipt_email`.
 * The server keeps it as the checkout e-mail that pre-fills the sign-in step
 * after payment.
 */
interface ReceiptEmailRequestState {
  pending: ((email: string | null) => void) | null;
}

export const useReceiptEmailRequest = create<ReceiptEmailRequestState>(() => ({ pending: null }));

/** Resolves with the address, or null when the person closed the dialog. */
export function requestReceiptEmail(): Promise<string | null> {
  return new Promise((resolve) => {
    useReceiptEmailRequest.setState({
      pending: (email) => {
        useReceiptEmailRequest.setState({ pending: null });
        resolve(email);
      },
    });
  });
}

/** True when neither the account nor an earlier checkout gave an address. */
export function needsReceiptEmail(): boolean {
  const user = queryClient.getQueryData<User>(cacheKeys.user);
  const signIn = queryClient.getQueryData<SignInState | null>(cacheKeys.signIn);
  return !!user && !user.email && !signIn?.sign_in_prefill_email;
}
